import React from 'react';
import { useTranslation } from 'react-i18next';
import Hero from '../components/Hero';
import ImpactStats from '../components/ImpactStats';
import PartnersSection from '../components/PartnersSection';
import { HeroSection, NewsArticle } from '../types';
import './Home.css';

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();
  const showAlbanianSubtitles = i18n.language?.startsWith('sq');

  const heroData: HeroSection = {
    title: t('hero.title'),
    subtitle: t('hero.subtitle'),
    description: t('hero.description'),
    ctaText: '',
    ctaLink: ''
  };


  const latestNews: NewsArticle[] = [
    {
      id: 'summer-2026',
      title: t('news.articles.summer2026.title'),
      excerpt: t('news.articles.summer2026.excerpt'),
      content: t('news.articles.summer2026.content'),
      publishDate: '2026-09-06',
      author: t('news.authors.forum'),
      image: '/events/summer-meeting-2026-invitation.jpg'
    },
    {
      id: '1',
      title: t('news.articles.secondEdition.title'),
      excerpt: t('news.articles.secondEdition.excerpt'),
      content: t('news.articles.secondEdition.content'),
      publishDate: '2025-11-05',
      author: t('news.authors.rotaryClub')
    },
    {
      id: 'first-edition-success',
      title: t('news.firstEdition.title'),
      excerpt: t('news.firstEdition.excerpt'),
      content: t('news.firstEdition.content'),
      publishDate: '2024-09-12',
      author: t('news.authors.forum'),
      image: '/First edition announcement.png'
    },
    {
      id: '2',
      title: t('news.articles.success2024.title'),
      excerpt: t('news.articles.success2024.excerpt'),
      content: t('news.articles.success2024.excerpt'),
      publishDate: '2024-09-12',
      author: 'Telegrafi',
      source: 'https://telegrafi.com/en/peace-has-no-alternatives%2C-the-first-edition-of-the-peace-forum-in-Peje-is-considered-successful/'
    },
    {
      id: '3',
      title: t('news.articles.ukraine.title'),
      excerpt: t('news.articles.ukraine.excerpt'),
      content: t('news.articles.ukraine.excerpt'),
      publishDate: '2024-09-11',
      author: 'Telegrafi',
      source: 'https://telegrafi.com/en/Stebljanko-at-the-peace-forum-in-Peje%2C-Ukraine-remains-strong-despite-the-economic-losses/'
    },
    {
      id: '4',
      title: t('news.articles.ai.title'),
      excerpt: t('news.articles.ai.excerpt'),
      content: t('news.articles.ai.excerpt'),
      publishDate: '2024-09-11',
      author: 'Telegrafi',
      source: 'https://telegrafi.com/en/artificial-intelligence-and-media-influence-hajrullahu-journalism-has-a-decisive-role-in-educating-the-public-about-it/'
    },
    {
      id: '5',
      title: t('news.articles.university.title'),
      excerpt: t('news.articles.university.excerpt'),
      content: t('news.articles.university.excerpt'),
      publishDate: '2024-09-11',
      author: 'Telegrafi',
      source: 'https://telegrafi.com/en/the-rector-of-Haxhi-Zeka-Krasniqi-University%2C-the-conclusions-from-the-peace-forum-are-also-important-for-the-academic-field/'
    },
    {
      id: '6',
      title: t('news.articles.conflictPrevention.title'),
      excerpt: t('news.articles.conflictPrevention.excerpt'),
      content: t('news.articles.conflictPrevention.excerpt'),
      publishDate: '2024-09-11',
      author: 'Telegrafi',
      source: 'https://telegrafi.com/en/peace-forum-in-Peje-Asllani%2C-our-role-is-to-prevent-conflicts-and-build-peace/'
    },
    {
      id: '7',
      title: t('news.articles.firstEdition.title'),
      excerpt: t('news.articles.firstEdition.excerpt'),
      content: t('news.articles.firstEdition.excerpt'),
      publishDate: '2024-09-11',
      author: 'Telegrafi',
      source: 'https://telegrafi.com/en/per-te-pare-ne-peje-mbahet-forumi-paqes-teme-ndertimi-paqes-ne-nje-bote-rivaliteti/'
    }
  ];

  return (
    <div className="home">
      <Hero hero={heroData} />

      {/* Upcoming Summer Meeting 2026 */}
      <section className="upcoming-event-section section">
        <div className="container">
          <div className="upcoming-event-layout">
            <div className="upcoming-event-copy">
              <p className="upcoming-event-eyebrow">{t('hero.upcomingEvent.eyebrow')}</p>
              <p className="upcoming-event-motto">{t('events.summer2026.motto')}</p>
              <h2>{t('events.summer2026.title')}</h2>
              <p className="upcoming-event-date">{t('events.summer2026.date')}</p>
              <p className="upcoming-event-description">{t('events.summer2026.excerpt')}</p>
              <p className="upcoming-event-theme">"{t('events.summer2026.roundtable.theme')}"</p>
              <a href="/events" className="btn btn-primary">
                {t('hero.upcomingEvent.cta')}
              </a>
            </div>
            <a href="/events" className="upcoming-event-image">
              <img
                src="/events/summer-meeting-2026-invitation.jpg"
                alt={t('events.summer2026.invitationCaption')}
              />
            </a>
          </div>
        </div>
      </section>
      
      {/* Second Edition Success with Keynote Video */}
      <section className="event-countdown-video section">
        <div className="container">
          <div className="countdown-content">
            <div className="countdown-text">
              <h2>{t('hero.success.title')}</h2>
              <p className="countdown-subtitle">{t('hero.success.subtitle')}</p>
              <p className="countdown-date">{t('hero.success.date')}</p>
              <p className="success-description">{t('hero.success.description')}</p>
            </div>

            <div className="video-container">
              <div className="video-wrapper">
                <video 
                  key={`keynote-${showAlbanianSubtitles ? 'sq' : 'en'}`}
                  controls 
                  className="video-player"
                  poster="/Peja Peace Forum 2nd Edition/General Wesley Clark.jpg"
                  preload="metadata"
                  width="100%"
                  height="auto"
                  crossOrigin="anonymous"
                >
                  <source src="/videos/wesley-clark-keynote.mp4" type="video/mp4" />
                  {showAlbanianSubtitles && (
                    <track
                      kind="subtitles"
                      src="/videos/wesley-clark-keynote.sq.vtt"
                      srcLang="sq"
                      label="Shqip"
                      default
                    />
                  )}
                  {t('video.browserNotSupported')}
                </video>
              </div>
              <p className="keynote-caption">{t('video.keynote.caption')}</p>
            </div>
            
            <div className="countdown-actions">
              <a href="/editions" className="btn btn-primary">
                {t('hero.success.ctaEditions')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section section">
        <div className="container">
          <div className="mission-content">
            <div className="mission-text">
              <h2>{t('mission.title')}</h2>
              <p>{t('mission.description')}</p>
            </div>
            <div className="vision-text">
              <h2>{t('vision.title')}</h2>
              <p>{t('vision.description')}</p>
            </div>
          </div>
          <div className="mission-vision-actions">
            <a href="/about" className="btn btn-primary">
              {t('mission.learnMore')}
            </a>
          </div>
        </div>
      </section>


      {/* Latest News Section */}
      <section className="news-section section">
        <div className="container">
          <h2 className="text-center">{t('news.title')}</h2>
          <div className="news-grid">
            {latestNews.slice(0, 3).map((article) => (
              <div key={article.id} className="news-card">
                <h3 className="news-title">{article.title}</h3>
                <p className="news-excerpt">{article.excerpt}</p>
                <div className="news-actions">
                  <a href={`/article/${article.id}`} className="news-link">
                    {t('news.readMore')}
                  </a>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a href="/media/news" className="btn">
              {t('news.viewAll')}
            </a>
          </div>
        </div>
      </section>


      {/* Impact Stats Section */}
      <ImpactStats />

      {/* Partners Section */}
      <PartnersSection />

      {/* Call to Action Section */}
      <section className="cta-section section">
        <div className="container">
          <div className="cta-content text-center">
            <h2>{t('cta.title')}</h2>
            <p>{t('cta.description')}</p>
            <div className="cta-buttons">
              <a href="/contact" className="btn">
                {t('cta.getInvolved')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
